import React, { useEffect, useState, useCallback } from "react";
import { Landmark, Users2, ShieldAlert, UserCheck, Settings2, Megaphone, Pause, Play, Trash2, Edit, Check, X, IndianRupee, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Toranam } from "@/components/Ornaments";
import AdminPayments from "@/components/AdminPayments";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";

export default function Admin() {
  const { t, refreshSettings } = useApp();
  const [stats, setStats] = useState({});
  const [temples, setTemples] = useState([]);
  const [archakas, setArchakas] = useState([]);
  const [dups, setDups] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [broadcast, setBroadcast] = useState({ title: "", body: "", send_email: false });
  const [editT, setEditT] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/temples").then(({ data }) => setTemples(data)).catch(() => {});
    api.get("/admin/archakas").then(({ data }) => setArchakas(data)).catch(() => {});
    api.get("/admin/duplicate-flags").then(({ data }) => setDups(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const decide = async (aid, decision) => {
    let reason = "";
    if (decision === "rejected") { reason = window.prompt("తిరస్కరణ కారణం:") || "వివరాలు అసంపూర్ణం"; }
    await api.patch(`/admin/archakas/${aid}/decision`, { decision, reason });
    toast.success(decision === "approved" ? "ఆమోదించబడింది ✅" : "తిరస్కరించబడింది");
    loadAll();
  };
  const setStatus = async (tid, status) => { await api.patch(`/temples/${tid}/status`, { status }); toast.success("స్థితి మార్చబడింది"); loadAll(); };
  const PERM = "ఇది శాశ్వతంగా తొలగించబడుతుంది, మళ్ళీ తిరిగి రాదు. ఖచ్చితంగా తొలగించాలా?";
  const del = async (tid) => { if (!window.confirm(PERM)) return; await api.delete(`/temples/${tid}`); toast.success("శాశ్వతంగా తొలగించబడింది"); loadAll(); };
  const delArchaka = async (aid) => { if (!window.confirm(PERM)) return; await api.delete(`/admin/archakas/${aid}`); toast.success("శాశ్వతంగా తొలగించబడింది"); loadAll(); };
  const saveTemple = async () => {
    setBusy(true);
    try { await api.put(`/temples/${editT.id}`, { ...editT, salary: parseInt(editT.salary) || 0 }); toast.success("సవరించబడింది ✅"); setEditT(null); loadAll(); }
    catch (e) { toast.error("లోపం"); } finally { setBusy(false); }
  };
  const saveSettings = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", settings); await refreshSettings(); toast.success("సెట్టింగ్‌లు సేవ్ అయ్యాయి ✅"); }
    catch (e) { toast.error("లోపం"); } finally { setBusy(false); }
  };
  const sendBroadcast = async () => {
    if (!broadcast.title || !broadcast.body) return toast.error("శీర్షిక & సందేశం అవసరం");
    setBusy(true);
    try { const { data } = await api.post("/admin/broadcast", broadcast); toast.success(`${data.recipients} వినియోగదారులకు పంపబడింది 📢`); setBroadcast({ title: "", body: "", send_email: false }); }
    catch (e) { toast.error("లోపం"); } finally { setBusy(false); }
  };
  const resolveDup = async (fid) => { await api.patch(`/admin/duplicate-flags/${fid}/resolve`); loadAll(); };

  const sset = (k, v) => setSettings((p) => ({ ...p, [k]: v }));
  const eset = (k, v) => setEditT((p) => ({ ...p, [k]: v }));

  const statCards = [
    ["దేవాలయాలు", stats.temples || 0, Landmark], ["ఆమోదిత అర్చకులు", stats.archakas_approved || 0, UserCheck],
    ["పెండింగ్ అర్చకులు", stats.archakas_pending || 0, Users2], ["వినియోగదారులు", stats.users || 0, Users2],
    ["నకిలీ హెచ్చరికలు", stats.duplicates || 0, ShieldAlert], ["ఆదాయం (₹)", stats.revenue || 0, IndianRupee],
  ];

  return (
    <div className="parchment-texture min-h-screen">
      <div className="bg-gradient-to-r from-maroon to-saffron py-8 text-center">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white flex items-center justify-center gap-2"><Settings2 className="w-7 h-7" /> {t("admin_panel")}</h1>
        <p className="text-marigold font-telugu mt-1">సూపర్ అడ్మిన్ · బాచరాజు వరుణ్ కుమార్</p>
      </div>
      <Toranam />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          {statCards.map(([l, n, Icon], i) => (
            <Card key={i} className="p-3 bg-surface gold-border text-center" data-testid={`stat-${i}`}>
              <Icon className="w-5 h-5 mx-auto text-saffron mb-1" /><div className="text-xl font-bold text-maroon">{n}</div><div className="text-[10px] text-muted-foreground font-telugu">{l}</div>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="temples">
          <TabsList className="flex flex-wrap h-auto bg-marigold/15 gap-1 p-1">
            <TabsTrigger value="temples" data-testid="tab-temples" className="font-telugu">🛕 దేవాలయాలు</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="tab-approvals" className="font-telugu">✅ అర్చక ఆమోదాలు {stats.archakas_pending ? <Badge className="ml-1 bg-saffron text-white">{stats.archakas_pending}</Badge> : null}</TabsTrigger>
            <TabsTrigger value="fraud" data-testid="tab-fraud" className="font-telugu">🚨 మోసం నిరోధం</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="font-telugu">👥 వినియోగదారులు</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" className="font-telugu">💰 చెల్లింపులు</TabsTrigger>
            <TabsTrigger value="cms" data-testid="tab-cms" className="font-telugu">⚙️ CMS</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="tab-broadcast" className="font-telugu">📢 ప్రసారం</TabsTrigger>
          </TabsList>

          {/* Temples manager */}
          <TabsContent value="temples" className="mt-4 space-y-3">
            {temples.map((tm) => (
              <Card key={tm.id} className="p-4 bg-surface gold-border flex items-center justify-between flex-wrap gap-2" data-testid={`admin-temple-${tm.id}`}>
                <div>
                  <div className="font-serifte font-bold text-maroon">{tm.name} <Badge variant="outline" className="ml-1 text-[10px]">{tm.source}</Badge></div>
                  <div className="text-xs text-muted-foreground font-telugu">{tm.deity} · {tm.village}, {tm.district} · ₹{tm.salary} · {tm.contact_phone}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={tm.status === "active" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>{tm.status}</Badge>
                  <Button size="sm" variant="outline" data-testid={`edit-temple-${tm.id}`} onClick={() => setEditT({ ...tm })} className="h-8"><Edit className="w-3.5 h-3.5" /></Button>
                  {tm.status === "active" ? <Button size="sm" variant="outline" data-testid={`pause-${tm.id}`} onClick={() => setStatus(tm.id, "paused")} className="h-8"><Pause className="w-3.5 h-3.5" /></Button>
                    : <Button size="sm" variant="outline" data-testid={`resume-${tm.id}`} onClick={() => setStatus(tm.id, "active")} className="h-8"><Play className="w-3.5 h-3.5" /></Button>}
                  <Button size="sm" variant="outline" data-testid={`delete-${tm.id}`} onClick={() => del(tm.id)} className="h-8 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            ))}
          </TabsContent>

          {/* Approvals */}
          <TabsContent value="approvals" className="mt-4 space-y-3">
            {archakas.filter((a) => a.status === "pending").length === 0 && <p className="text-center py-8 font-telugu text-muted-foreground">పెండింగ్ ప్రొఫైల్స్ లేవు</p>}
            {archakas.map((a) => (
              <Card key={a.id} className="p-4 bg-surface gold-border" data-testid={`admin-archaka-${a.id}`}>
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12"><AvatarImage src={a.photo} /><AvatarFallback className="bg-maroon text-marigold">{a.name.slice(0, 2)}</AvatarFallback></Avatar>
                  <div className="flex-1">
                    <div className="font-serifte font-bold text-maroon">{a.name} <Badge className={a.status === "approved" ? "bg-green-100 text-green-800" : a.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>{t(a.status)}</Badge></div>
                    <div className="text-xs text-muted-foreground font-telugu">🪷 {a.gotram} {a.shakha && `| ${a.shakha}`} · {a.agama} · {a.district} · {a.mobile}</div>
                    {a.bio && <p className="text-xs text-maroon font-telugu mt-1">{a.bio}</p>}
                    {a.mantra_audio && <audio controls src={a.mantra_audio} className="mt-2 h-8" />}
                  </div>
                  {a.status !== "approved" && <Button size="sm" data-testid={`approve-${a.id}`} onClick={() => decide(a.id, "approved")} className="bg-green-600 hover:bg-green-700 text-white h-8"><Check className="w-4 h-4 mr-1" />{t("approve")}</Button>}
                  {a.status !== "rejected" && <Button size="sm" variant="outline" data-testid={`reject-${a.id}`} onClick={() => decide(a.id, "rejected")} className="text-destructive h-8"><X className="w-4 h-4 mr-1" />{t("reject")}</Button>}
                  <Button size="sm" variant="outline" data-testid={`delete-archaka-${a.id}`} onClick={() => delArchaka(a.id)} className="text-destructive h-8" title="శాశ్వతంగా తొలగించు"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            ))}
          </TabsContent>

          {/* Fraud */}
          <TabsContent value="fraud" className="mt-4 space-y-3">
            {dups.length === 0 && <p className="text-center py-8 font-telugu text-muted-foreground">నకిలీ ప్రయత్నాలు లేవు</p>}
            {dups.map((d) => (
              <Card key={d.id} className="p-4 bg-red-50 border border-red-300 flex items-center justify-between flex-wrap gap-2" data-testid={`dup-${d.id}`}>
                <div><div className="font-semibold text-red-800 font-telugu">{d.temple_name} · {d.deity}</div><div className="text-xs text-red-700 font-telugu">{d.village} · {d.contact_phone} · {d.resolved ? "పరిష్కరించబడింది" : "సమీక్షలో"}</div></div>
                {!d.resolved && <Button size="sm" variant="outline" data-testid={`resolve-dup-${d.id}`} onClick={() => resolveDup(d.id)}>పరిష్కరించు</Button>}
              </Card>
            ))}
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-4 space-y-2">
            {users.map((u) => (
              <Card key={u.user_id} className="p-3 bg-surface gold-border flex items-center justify-between text-sm">
                <div><span className="font-medium text-maroon font-telugu">{u.name}</span> <span className="text-xs text-muted-foreground">{u.email} · {u.unique_id}</span></div>
                <Badge variant="outline" className="font-telugu">{u.role}{u.role === "temple" ? ` · ${u.credits} క్రెడిట్స్` : ""}</Badge>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="payments" className="mt-4"><AdminPayments onChange={loadAll} /></TabsContent>

          {/* CMS */}
          <TabsContent value="cms" className="mt-4">
            <Card className="p-6 bg-surface gold-border space-y-3 max-w-2xl">
              <h3 className="font-serifte font-bold text-maroon text-lg">సైట్ సెట్టింగ్‌లు (CMS)</h3>
              {[["site_name", "సైట్ పేరు (English)"], ["site_name_te", "సైట్ పేరు (తెలుగు)"], ["tagline_te", "ట్యాగ్‌లైన్ (తెలుగు)"], ["founder_name", "వ్యవస్థాపకుని పేరు"], ["helpline_phone", "హెల్ప్‌లైన్ ఫోన్"], ["support_email", "సపోర్ట్ ఇమెయిల్"]].map(([k, l]) => (
                <div key={k}><Label className="font-telugu text-maroon text-sm">{l}</Label><Input data-testid={`cms-${k}`} value={settings[k] || ""} onChange={(e) => sset(k, e.target.value)} className="gold-border font-telugu mt-1" /></div>
              ))}
              <div className="grid grid-cols-3 gap-2">
                {[["archaka_monthly_fee", "అర్చక నెలవారీ ఫీజు (₹)"]].map(([k, l]) => (
                  <div key={k}><Label className="font-telugu text-maroon text-xs">{l}</Label><Input data-testid={`cms-${k}`} type="number" value={settings[k] || 0} onChange={(e) => sset(k, parseInt(e.target.value) || 0)} className="gold-border mt-1" /></div>
                ))}
              </div>
              <div className="pt-3 border-t border-goldline/30 space-y-3">
                <h4 className="font-serifte font-bold text-maroon">QR చెల్లింపు వివరాలు</h4>
                <FileUpload label="చెల్లింపు QR కోడ్ ఫోటో" accept="image/*" value={settings.payment_qr_url || ""} onChange={(v) => sset("payment_qr_url", v)} testid="cms-payment-qr" />
                {[["payment_upi_id", "UPI ID (ఉదా: name@ybl)"], ["payment_payee_name", "ఖాతాదారుని పేరు"]].map(([k, l]) => (
                  <div key={k}><Label className="font-telugu text-maroon text-sm">{l}</Label><Input data-testid={`cms-${k}`} value={settings[k] || ""} onChange={(e) => sset(k, e.target.value)} className="gold-border mt-1" /></div>
                ))}
                <p className="text-xs text-muted-foreground font-telugu">మార్పుల తర్వాత కింద సేవ్ నొక్కండి. screenshot లు WhatsApp కు హెల్ప్‌లైన్ నంబర్‌కే వస్తాయి.</p>
              </div>
              <Button data-testid="cms-save" onClick={saveSettings} disabled={busy} className="btn-sacred border-0 font-telugu">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save")}</Button>
            </Card>
          </TabsContent>

          {/* Broadcast */}
          <TabsContent value="broadcast" className="mt-4">
            <Card className="p-6 bg-surface gold-border space-y-3 max-w-2xl">
              <h3 className="font-serifte font-bold text-maroon text-lg flex items-center gap-2"><Megaphone className="w-5 h-5 text-saffron" />ప్రసార హెచ్చరిక</h3>
              <div><Label className="font-telugu text-maroon text-sm">శీర్షిక</Label><Input data-testid="bc-title" value={broadcast.title} onChange={(e) => setBroadcast((p) => ({ ...p, title: e.target.value }))} className="gold-border font-telugu mt-1" /></div>
              <div><Label className="font-telugu text-maroon text-sm">సందేశం</Label><Textarea data-testid="bc-body" value={broadcast.body} onChange={(e) => setBroadcast((p) => ({ ...p, body: e.target.value }))} className="gold-border font-telugu mt-1" rows={3} /></div>
              <div className="flex items-center gap-2"><Switch data-testid="bc-email" checked={broadcast.send_email} onCheckedChange={(v) => setBroadcast((p) => ({ ...p, send_email: v }))} /><span className="font-telugu text-sm text-maroon">ఇమెయిల్ ద్వారా కూడా పంపండి</span></div>
              <Button data-testid="bc-send" onClick={sendBroadcast} disabled={busy} className="btn-sacred border-0 font-telugu">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "అందరికీ పంపండి 📢"}</Button>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit temple dialog */}
      <Dialog open={!!editT} onOpenChange={(o) => !o && setEditT(null)}>
        <DialogContent className="max-w-lg bg-surface max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-maroon">దేవాలయ ఖాళీ సవరించండి</DialogTitle></DialogHeader>
          {editT && (
            <div className="space-y-2">
              {[["name", "పేరు"], ["deity", "దేవత"], ["village", "గ్రామం"], ["mandal", "మండలం"], ["district", "జిల్లా"], ["contact_phone", "ఫోన్"], ["contact_name", "సంప్రదింపు వ్యక్తి"], ["morning_shift", "ఉదయం షిఫ్ట్"], ["evening_shift", "సాయంత్రం షిఫ్ట్"], ["map_link", "మ్యాప్ లింక్"]].map(([k, l]) => (
                <div key={k}><Label className="font-telugu text-maroon text-xs">{l}</Label><Input data-testid={`et-${k}`} value={editT[k] || ""} onChange={(e) => eset(k, e.target.value)} className="gold-border font-telugu mt-0.5" /></div>
              ))}
              <div><Label className="font-telugu text-maroon text-xs">వేతనం (₹)</Label><Input type="number" value={editT.salary || 0} onChange={(e) => eset("salary", e.target.value)} className="gold-border mt-0.5" /></div>
              <div className="grid grid-cols-3 gap-2 py-1">
                <label className="flex items-center gap-1 text-xs font-telugu"><Switch checked={!!editT.ddn} onCheckedChange={(v) => eset("ddn", v)} /> DDN</label>
                <label className="flex items-center gap-1 text-xs font-telugu"><Switch checked={!!editT.free_food} onCheckedChange={(v) => eset("free_food", v)} /> భోజనం</label>
                <label className="flex items-center gap-1 text-xs font-telugu"><Switch checked={!!editT.accommodation} onCheckedChange={(v) => eset("accommodation", v)} /> వసతి</label>
              </div>
              <Button data-testid="et-save" onClick={saveTemple} disabled={busy} className="w-full btn-sacred border-0 font-telugu">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
